import api from "@modules/api/api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { vehicleValidation } from "@talladega/helpers";
import { useUppercaseAlphaNumericMask } from "@talladega/hooks";
import { VehicleLookupResponse } from "../types/VehicleLookupResponse";
import vehicleQueryKeys from "../vehicleQueryKeys";

const getVehicleDetails = (
  { vin, vehicleSearchString }: { vin?: string; vehicleSearchString?: string },
  key: string
): Promise<VehicleLookupResponse> => {
  const params = new URLSearchParams();
  if (vin) {
    params.append("vin", vin);
  }

  if (vehicleSearchString) {
    params.append("vehicleSearchString", vehicleSearchString.trim());
  }

  params.append("key", key);

  return api.get<VehicleLookupResponse>(
    `api/vehicles/vehicle-autocomplete?${params.toString()}`
  );
};

export function useVinDecodeLookup(
  vin: string,
  key: string,
  enabled?: boolean
) {
  const { maskPipe: maskUppercaseAlphaNumericPipe } =
    useUppercaseAlphaNumericMask();

  const { data, error, isFetching } = useQuery({
    queryKey: vehicleQueryKeys.vehicleLookupSearchByVin(vin),
    queryFn: () => getVehicleDetails({ vin }, key),
    enabled:
      enabled !== false &&
      vehicleValidation.vin.validVin(maskUppercaseAlphaNumericPipe(vin) ?? ""),
    staleTime: Infinity,
  });

  return {
    error,
    vehiclesFound: data?.vehicles ?? [],
    isFetching,
  };
}

export function useVehicleLookupByName(
  vehicleSearchString: string,
  key: string,
  enabled = true
) {
  const minQueryLength = 3;

  const { data, error } = useQuery({
    queryKey:
      vehicleQueryKeys.vehicleLookupSearchByVehicle(vehicleSearchString),
    queryFn: () => getVehicleDetails({ vehicleSearchString }, key),
    enabled:
      !!vehicleSearchString?.trim() &&
      vehicleSearchString.length >= minQueryLength &&
      enabled,
    placeholderData: keepPreviousData,
  });

  return {
    vehicles: data?.vehicles ?? [],
    isLoading: !error && !data,
    error,
  };
}
